import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  ProceduresAmendmentChange,
  ProceduresAmendmentProcedureValues,
  ProceduresAmendmentRow,
} from "@/encounters/types/procedures";

const CHANGE_LABELS: Record<ProceduresAmendmentChange, string> = {
  added: "Added",
  modified: "Updated",
  removed: "Removed",
};

// The fields the procedures tab records about a procedure, in the order the picker asks for them.
const DIFF_FIELDS: { label: string; field: keyof ProceduresAmendmentProcedureValues }[] = [
  { label: "Procedure Code", field: "code" },
  { label: "Units", field: "units" },
  { label: "Modifiers", field: "modifiers" },
  { label: "Provider Notes", field: "providerNotes" },
  { label: "Biller Notes", field: "billerNotes" },
  { label: "Diagnoses", field: "diagnoses" },
];

type ProceduresAmendmentDiffColumnProps = {
  title: string;
  row: ProceduresAmendmentRow;
  /** The previous column shows what the amendment took out; the amended one what it put in. */
  showRemoved?: boolean;
};

const ProceduresAmendmentDiffColumn = ({
  title,
  row,
  showRemoved = false,
}: ProceduresAmendmentDiffColumnProps) => (
  <Paper p="md" withBorder>
    <Stack>
      <Title mb="lg" order={4} c="gray.6">
        {title}
      </Title>
      <Stack gap="md">
        {DIFF_FIELDS.map(({ label, field }) => (
          <Stack gap="0" key={field}>
            <TextWrapper fw={600}>{label}</TextWrapper>
            <WordsDiff
              showRemoved={showRemoved}
              current={row.current[field]}
              previous={row.previous[field]}
            />
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Paper>
);

type ProceduresAmendmentDiffCardProps = {
  row: ProceduresAmendmentRow;
};

/**
 * One procedure the amendment changed, as the two sides the rest of the amendment view uses.
 *
 * The heading names the procedure from whichever side has it: a removed procedure only has a code on
 * the previous side, and an added one only on the amended side.
 */
const ProceduresAmendmentDiffCard = ({ row }: ProceduresAmendmentDiffCardProps) => (
  <Paper p="md" withBorder data-testid="procedures-amendment-diff-card">
    <Group mb="md" gap="sm">
      <TextWrapper fw={600}>{row.previous.code || row.current.code}</TextWrapper>
      <TextWrapper fz="sm" c="dimmed" data-testid="procedures-amendment-diff-change">
        {CHANGE_LABELS[row.change]}
      </TextWrapper>
    </Group>
    <Group grow align="stretch">
      <ProceduresAmendmentDiffColumn row={row} showRemoved title="Previous" />
      <ProceduresAmendmentDiffColumn row={row} title="Amended" />
    </Group>
  </Paper>
);

export default ProceduresAmendmentDiffCard;
